import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Divider,
  Drawer,
  Row,
  Col,
  message,
} from 'antd';
import axios from 'axios';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import DivisionSelector from '../../common/inputs/DivisionSelector';
import HoverHelp from '../../common/HoverHelp';

import CustomExportTable from '../../reports/Exports/CustomExportsTable';

import ExportsColumnHeader from './ExportsColumnHeader';
import ExportFormsConfig from './ExportFormsConfig';
import ApprovalControls from './ApprovalControls';

import {
  CUSTOM_EXPORT_PROP_TYPE,
} from './exports.constants';

import {
  createCustomExport,
  updateCustomExport,
} from '../../reports/state/reports.actions';
import { getTemplates } from '../../forms/state/forms.actions';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import UserAssignmentSelector from '../../forms/FormWorkflows/selectors/UserAssignmentSelector';
import useCustomExportPermissions from '../../reports/Exports/useCustomExportPermissions';
import { safeExtractFromObject } from '../../helpers/helpers';

const summarizeEmployeesHelp = 'Select this checkbox if you want multiple entries for a user to be combined into one line. Overrides the Summarize Days checkbox';
const summarizeDaysHelp = 'Select this checkbox if you want multiple entries for a user in the same day to be combined into one line. Requires a Date column';
const summarizeProjectHelp = 'Select this checkbox if you want the summarized data to be broken down by project.';
const stepExistsInTemplate = (formTemplate, stepId) => {
  if (!stepId) return true;
  const raw = formTemplate?.jsonFormData || formTemplate?.formData;
  if (!raw) return true;
  try {
    const wf = typeof raw === 'string' ? JSON.parse(raw).workflow : raw.workflow;
    if (!wf?.nodes) return true;
    return wf.nodes.some((n) => n.id === stepId);
  } catch {
    return true;
  }
};
function Exports({
  selectedExport,
  visible,
  onClose,
  type,
}) {
  const isTimeTracking = type === 'timeEntries';
  const isForms = type === 'forms';

  const dispatch = useDispatch();

  const formTemplates = useSelector((state) => state.forms.templates);
  const userCustomFields = useSelector((state) => state.users.customFieldTemplate);
  const settings = useSelector((state) => safeExtractFromObject(state, 'settings.company.settings'));
  const {
    enableTimecardSubmission: showTimecardApprovalFilter = false,
  } = settings;

  const {
    selectedPermissions,
    permanentUserIds,
    buildAccessPayload,
    hydratePermissionsFromExport,
    clearPermissions,
    setSelectedPermissions,
  } = useCustomExportPermissions(selectedExport);

  const [columns, setColumns] = useState(selectedExport?.columns ?? []);
  const [title, setTitle] = useState(selectedExport?.title ?? null);
  const [approvedOnly, setApprovedOnly] = useState(!!selectedExport?.approvedOnly);
  const [
    requiredApprovalStepId,
    setRequiredApprovalStepId,
  ] = useState(selectedExport?.requiredApprovalStepId ?? null);
  const [
    summarizeEmployees,
    setSummarizeEmployees,
  ] = useState(!!selectedExport?.summarizeEmployees);
  const [summarizeDays, setSummarizeDays] = useState(!!selectedExport?.summarizeDays);
  const [
    generateBatchNumber,
    setGenerateBatchNumber,
  ] = useState(!!selectedExport?.generateBatchNumber);
  const [
    addLineNumbers,
    setAddLineNumbers,
  ] = useState(!!selectedExport?.addLineNumbers);
  const [
    generateFormNumber,
    setGenerateFormNumber,
  ] = useState(!!selectedExport?.generateFormNumber);
  const [
    summarizeProject,
    setSummarizeProject,
  ] = useState(!!selectedExport?.summarizeProject);
  const [
    formTemplateId,
    setFormTemplateId,
  ] = useState(selectedExport?.formTemplateId);
  const [divisionId, setDivisionId] = useState(selectedExport?.divisionId ?? null);
  const [hasEclipse, setHasEclipse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateFilterFieldId, setDateFilterFieldId] = useState(
    selectedExport?.dateFilterFieldId ?? 'lastUpdated',
  );
  const [includeZeroHours, setIncludeZeroHours] = useState(!!selectedExport?.includeZeroHours);

  const userCustomFieldIds = useMemo(() => (
    new Set(userCustomFields?.fields?.map((field) => field.id) ?? [])
  ), [userCustomFields]);

  const effectiveApprovedOnly = isTimeTracking
    ? showTimecardApprovalFilter && approvedOnly
    : approvedOnly;

  const onSubmit = useCallback(async () => {
    if (!columns || columns.length === 0) {
      onClose();
      return;
    }
    setLoading(true);
    const access = buildAccessPayload();
    const payload = {
      title,
      columns,
      approvedOnly: effectiveApprovedOnly,
      requiredApprovalStepId,
      summarizeEmployees,
      summarizeDays,
      summarizeProject,
      generateBatchNumber,
      addLineNumbers,
      generateFormNumber,
      divisionId,
      dateFilterFieldId,
      includeZeroHours,
      access,
    };
    if (!selectedExport) {
      payload.type = type;
      payload.formTemplateId = formTemplateId;
    }
    const action = selectedExport
      ? updateCustomExport(selectedExport.id, payload)
      : createCustomExport(payload);
    if (await dispatch(action)) {
      onClose();
    }
    setLoading(false);
  }, [
    dispatch,
    selectedExport,
    columns,
    title,
    effectiveApprovedOnly,
    requiredApprovalStepId,
    summarizeEmployees,
    summarizeDays,
    summarizeProject,
    generateBatchNumber,
    addLineNumbers,
    generateFormNumber,
    formTemplateId,
    divisionId,
    dateFilterFieldId,
    includeZeroHours,
    buildAccessPayload,
    onClose,
    type,
  ]);

  const onTitleChanged = useCallback((e) => {
    const { target: { value } = {} } = e;
    setTitle(value);
  }, []);

  const onGenerateBatchNumberChanged = useCallback((checked) => {
    if (!checked) setGenerateFormNumber(false);
    setGenerateBatchNumber(checked);
  }, []);

  const onAddLineNumbersChanged = useCallback((checked) => {
    if (!checked) setGenerateFormNumber(false);
    setAddLineNumbers(checked);
  }, []);

  const onSummarizeEmployeesChanged = useCallback((checked) => {
    setSummarizeEmployees(checked);
    if (checked) {
      setSummarizeDays(false);
      setColumns(
        columns.filter((col) => !(
          col?.field?.startsWith?.('field-') && !userCustomFieldIds.has(col?.field)
        )),
      );
    }
  }, [columns, userCustomFieldIds]);

  const onSummarizeDaysChanged = useCallback((checked) => {
    setSummarizeDays(checked);
    if (checked) {
      setSummarizeEmployees(false);
      setColumns(
        columns.filter((col) => !(
          col.field === 'type'
          || col.field === 'state'
          || (col?.field?.startsWith?.('field-') && !userCustomFieldIds.has(col?.field))
        )),
      );
    } else {
      setSummarizeProject(false);
    }
  }, [columns, userCustomFieldIds]);

  const onSummarizeProjectChanged = useCallback((checked) => {
    setSummarizeProject(checked);
    if (checked) {
      setColumns(
        columns.filter((col) => !(
          col?.field?.startsWith?.('field-') && !userCustomFieldIds.has(col?.field)
        )),
      );
    }
  }, [columns, userCustomFieldIds]);

  const onDivisionChanged = useCallback((newDivisionId) => {
    if (newDivisionId === divisionId) return;
    setDivisionId(newDivisionId);
    setColumns(
      columns.filter((col) => !(
        col?.field?.startsWith?.('field') && !userCustomFieldIds.has(col?.field)
      )),
    );
    setFormTemplateId();
  }, [divisionId, columns, userCustomFieldIds]);

  const previewConfig = useMemo(() => {
    const validCols = columns.filter((col) => col.title && (col.field || col.fields?.length));
    if (validCols.length === 0) return null;
    // Dont add batch number so we dont consume a number if they export here
    return {
      id: 'preview',
      title: title && title.length > 0 ? title : 'Export Preview',
      columns: validCols,
      approvedOnly: effectiveApprovedOnly,
      requiredApprovalStepId,
      summarizeEmployees,
      summarizeDays,
      summarizeProject,
      divisionId,
      addLineNumbers,
      type,
      formTemplateId,
      dateFilterFieldId,
      includeZeroHours,
    };
  }, [
    type,
    title,
    columns,
    divisionId,
    effectiveApprovedOnly,
    requiredApprovalStepId,
    summarizeEmployees,
    summarizeDays,
    summarizeProject,
    addLineNumbers,
    formTemplateId,
    dateFilterFieldId,
    includeZeroHours,
  ]);

  const shouldAddNumber = useMemo(() => {
    if (!isForms) return false;

    const {
      [formTemplateId]: {
        jsonFormData = '',
      } = {},
    } = formTemplates;

    let formData = {};

    try {
      formData = JSON.parse(jsonFormData);
    } catch (err) {
      formData = {};
    }

    const {
      collected: {
        number: {
          collect = false,
        } = {},
      } = {},
    } = formData;

    return collect;
  }, [formTemplateId, formTemplates, isForms]);

  useEffect(() => {
    if (selectedExport) {
      setTitle(selectedExport.title);
      setColumns(selectedExport.columns ?? []);
      setApprovedOnly(showTimecardApprovalFilter && !!selectedExport.approvedOnly);
      setRequiredApprovalStepId(selectedExport.requiredApprovalStepId ?? null);
      setSummarizeEmployees(!!selectedExport.summarizeEmployees);
      setSummarizeDays(!!selectedExport.summarizeDays);
      setSummarizeProject(!!selectedExport.summarizeProject);
      setGenerateBatchNumber(!!selectedExport.generateBatchNumber);
      setAddLineNumbers(!!selectedExport.addLineNumbers);
      setGenerateFormNumber(!!selectedExport.generateFormNumber);
      setDivisionId(selectedExport.divisionId);
      setFormTemplateId(selectedExport.formTemplateId);
      setDateFilterFieldId(selectedExport.dateFilterFieldId ?? 'lastUpdated');
      setIncludeZeroHours(!!selectedExport.includeZeroHours);
      hydratePermissionsFromExport();
      if (
        selectedExport.requiredApprovalStepId
        && !stepExistsInTemplate(
          formTemplates?.[selectedExport.formTemplateId],
          selectedExport.requiredApprovalStepId,
        )
      ) {
        setRequiredApprovalStepId(null);
        message.warning(
          'The previously selected approval step no longer exists in this workflow. Defaulting to “Any approval”.',
        );
      }
    }
  }, [selectedExport, showTimecardApprovalFilter]);

  useEffect(() => {
    if (!visible) {
      setColumns([]);
      setTitle();
      setApprovedOnly(false);
      setRequiredApprovalStepId(null);
      setSummarizeEmployees(false);
      setSummarizeDays(false);
      setSummarizeProject(false);
      setFormTemplateId();
      setGenerateBatchNumber(false);
      setAddLineNumbers(false);
      setGenerateFormNumber(false);
      setDivisionId();
      setHasEclipse(false);
      setDateFilterFieldId('lastUpdated');
      setIncludeZeroHours(false);
      clearPermissions();
    }
  }, [visible]);

  useEffect(() => {
    const loadHasEclipse = async () => {
      try {
        const { data: syncTime } = await axios.get('/eclipse/sync/time');
        setHasEclipse(!!syncTime);
      } catch (_err) {
        setHasEclipse(false);
      }
    };
    if (visible && isForms) {
      if (isForms) {
        dispatch(getTemplates());
      } else {
        loadHasEclipse();
      }
    }
  }, [visible, isForms]);

  const summarizeEnabled = useMemo(() => (
    columns.some((col) => col.field === 'date')
  ), [columns]);

  return (
    <Drawer
      title="Add Export"
      onClose={onClose}
      visible={visible}
      width="90%"
    >
      <Row align="middle" gutter={10}>
        <Col span={12}>
          <OnTraccrTextInput
            placeholder="Enter Export Title"
            onChange={onTitleChanged}
            value={title}
          />
        </Col>
        <Col>
          <DivisionSelector
            divisionId={divisionId}
            onChange={onDivisionChanged}
            allowClear={false}
            style={{ width: 250 }}
            disabled={isForms && selectedExport?.id}
          />
        </Col>
      </Row>
      {
        isTimeTracking
        && (
          <Row align="middle" gutter={10} style={{ marginTop: 10, marginBottom: 10 }}>
            {showTimecardApprovalFilter && (
              <Col>
                <OnTraccrCheckbox
                  value={approvedOnly}
                  onChange={setApprovedOnly}
                  label="Approved Hours Only?"
                />
              </Col>
            )}
            <Col style={showTimecardApprovalFilter ? { paddingLeft: 30 } : {}}>
              <OnTraccrCheckbox
                value={summarizeEmployees}
                onChange={onSummarizeEmployeesChanged}
                label="Summarize by Employee?"
              />
            </Col>
            <Col>
              <HoverHelp content={summarizeEmployeesHelp} placement="bottomRight" />
            </Col>
            <Col style={{ paddingLeft: 30 }}>
              <OnTraccrCheckbox
                value={summarizeDays && summarizeEnabled && !summarizeEmployees}
                onChange={onSummarizeDaysChanged}
                disabled={!summarizeEnabled || summarizeEmployees}
                label="Summarize Days?"
              />
            </Col>
            <Col>
              <HoverHelp content={summarizeDaysHelp} placement="bottomRight" />
            </Col>
            <Col style={{ paddingLeft: 30 }}>
              <OnTraccrCheckbox
                value={includeZeroHours}
                onChange={setIncludeZeroHours}
                label="Include Zero-Hour Entries?"
              />
            </Col>
            {summarizeDays && (
              <>
                <Col style={{ paddingLeft: 30 }}>
                  <OnTraccrCheckbox
                    value={summarizeProject && summarizeDays && summarizeEnabled}
                    onChange={onSummarizeProjectChanged}
                    disabled={!summarizeEnabled}
                    label="Summarize by Project?"
                  />
                </Col>
                <Col>
                  <HoverHelp content={summarizeProjectHelp} placement="bottomRight" />
                </Col>
              </>
            )}
          </Row>
        )
      }
      {
        isForms
        && (
          <ExportFormsConfig
            canChangeTemplate={!selectedExport?.id}
            divisionId={divisionId}
            formTemplateId={formTemplateId}
            onFormTemplateSelected={setFormTemplateId}
            approvedOnly={approvedOnly}
            onApprovedChanged={setApprovedOnly}
            dateFilterFieldId={dateFilterFieldId}
            onDateFilterSelected={setDateFilterFieldId}
            requiredApprovalStepId={requiredApprovalStepId}
            onRequiredApprovalSelected={setRequiredApprovalStepId}
          />
        )
      }
      <Row align="middle" gutter={10} style={{ marginTop: 10, marginBottom: 20 }}>
        <Col>
          <OnTraccrCheckbox
            value={generateBatchNumber}
            onChange={onGenerateBatchNumberChanged}
            label="Generate Batch Number"
          />
        </Col>
        <Col style={{ paddingLeft: 30 }}>
          <OnTraccrCheckbox
            value={addLineNumbers}
            onChange={onAddLineNumbersChanged}
            label="Add Line Numbers"
          />
        </Col>
        {
          generateBatchNumber && addLineNumbers && isForms && shouldAddNumber && (
            <OnTraccrCheckbox
              value={generateFormNumber}
              onChange={setGenerateFormNumber}
              label="Auto-Generate Form Numbers"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Enable this to set all exported form&apos;s numbers
                  to <code>[Batch Number]-[Line Number]</code>.
                </div>
              )}
            />
          )
        }
        {isForms && (
          <ApprovalControls
            formTemplateId={formTemplateId}
            approvedOnly={approvedOnly}
            onApprovedChanged={setApprovedOnly}
            requiredApprovalStepId={requiredApprovalStepId}
            onRequiredApprovalSelected={setRequiredApprovalStepId}
          />
        )}

      </Row>
      <Row gutter={10} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <UserAssignmentSelector
            divisions={divisionId ? [divisionId] : []}
            onChange={setSelectedPermissions}
            selected={selectedPermissions}
            mode="multiple"
            showPositions
            type="users"
            text="Select View Permissions:"
            disabled={!divisionId}
            permanentUserIds={permanentUserIds}
            allowClear={false}
          />
        </Col>
      </Row>
      <ExportsColumnHeader
        columns={columns}
        onColumnsChanged={setColumns}
        divisionId={divisionId}
        hasEclipse={hasEclipse}
        type={type}
        formTemplateId={formTemplateId}
        hasSummarize={summarizeDays || summarizeEmployees || summarizeProject}
      />
      <Divider />
      {previewConfig
        && (
          <CustomExportTable
            selectedExport={previewConfig}
            scroll="calc(100vh - 600px)"
          />
        )}
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
        canSubmit={!!title && !!divisionId && columns.length > 0}
        loading={loading}
      />
    </Drawer>
  );
}

Exports.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedExport: CUSTOM_EXPORT_PROP_TYPE,
  type: PropTypes.string.isRequired,
};

Exports.defaultProps = {
  selectedExport: null,
};

export default Exports;
